// Terceiro destino da telemetria, ao lado do GA4 e do Clarity. Quem chama é
// lib/analytics.ts — nenhuma tela importa este arquivo. Motivo: o app já tinha
// UMA camada tipada de eventos, e ter mais um destino não pode virar mais uma
// lista de nomes de evento.
//
// A divisão de trabalho: o GA responde "de onde veio a visita", o Clarity mostra
// a gravação de quem brigou com a tela, e o PostHog é a análise de produto —
// funil, retenção e comportamento por usuário identificado.
//
// A lib entra por import DINÂMICO: são ~50 kB que ninguém precisa antes da
// primeira pintura. Enquanto ela carrega, os eventos ficam numa fila curta e
// saem na ordem — sem isso, o page_view da carga inicial se perderia sempre.
//
// Sem VITE_POSTHOG_KEY (vite dev, teste, preview) tudo aqui é no-op silencioso,
// igual ao gtag ausente. Medição nunca derruba tela.
import type { PostHog } from "posthog-js";

const KEY = (import.meta.env.VITE_POSTHOG_KEY ?? "").trim();
// Região do projeto: us.i.posthog.com ou eu.i.posthog.com. Fica em env porque é
// escolha da conta, não do código — e um dia pode virar proxy no nosso domínio.
const HOST = (import.meta.env.VITE_POSTHOG_HOST ?? "https://us.i.posthog.com").trim();

let ph: PostHog | null = null;
let carregando = false;
// Curta de propósito: se a lib não carregou (rede ruim, bloqueador), guardar
// centenas de eventos só adia o descarte e segura memória à toa.
const fila: Array<(p: PostHog) => void> = [];
const FILA_MAX = 50;

function comPostHog(uso: (p: PostHog) => void): void {
  if (ph) {
    uso(ph);
    return;
  }
  if (carregando && fila.length < FILA_MAX) fila.push(uso);
}

export function initPostHog(): void {
  if (!KEY || carregando || ph) return;
  carregando = true;

  void import("posthog-js")
    .then(({ default: posthog }) => {
      posthog.init(KEY, {
        api_host: HOST,
        // Só quem faz login vira pessoa no PostHog. Person profile é o maior
        // multiplicador de custo da plataforma, e o Disk é um admin: quase todo
        // acesso é de gente logada, mas a tela de login não precisa de perfil.
        person_profiles: "identified_only",
        // Autocapture manda evento a cada clique em qualquer coisa: é o que
        // estoura a franquia mensal e enche o projeto de evento que ninguém lê.
        // Evento de produto entra nomeado, pelo `track()` de analytics.ts.
        autocapture: false,
        // Numa SPA o pageview automático não reconta a navegação client-side.
        // Quem manda é o AnalyticsListeners, a cada rota — mesma regra do GA.
        capture_pageview: false,
        // Erro já tem dono; ligar aqui só criaria uma segunda fila de exceções
        // para ninguém triar.
        capture_exceptions: false,
        // Gravação de sessão aqui é papel do Clarity, que já está no index.html.
        // Duas gravações da mesma tela é custo dobrado pelo mesmo dado.
        disable_session_recording: true,
        // Sem isto o PostHog baixa surveys.js em toda carga para perguntar se há
        // pesquisa a exibir — script e requisição para um produto que não usamos.
        disable_surveys: true,
      });
      ph = posthog;
      for (const uso of fila) uso(posthog);
      fila.length = 0;
    })
    .catch(() => {
      // Bloqueador de anúncio ou rede ruim. Solta a fila para não segurar
      // memória e volta ao estado de "sem telemetria", que é sempre aceitável.
      carregando = false;
      fila.length = 0;
    });
}

export function phCapture(nome: string, props: Record<string, unknown>): void {
  comPostHog((p) => p.capture(nome, props));
}

export function phPageView(path: string, title: string): void {
  comPostHog((p) =>
    p.capture("$pageview", {
      $current_url: window.location.origin + path,
      title,
    }),
  );
}

// O MESMO id que o back usa como distinct_id. Sem isso, o mesmo humano vira
// duas pessoas no PostHog e todo funil que cruza front e back mente.
//
// LGPD: só o id. Nome, e-mail e telefone não sobem — ver a regra de ouro em
// lib/analytics.ts, que aqui vale igual.
export function phIdentify(userId: string): void {
  comPostHog((p) => p.identify(userId));
}

// Logout: zera o distinct_id para a próxima sessão não herdar a identidade da
// anterior. No Disk isso não é hipótese: PA e vendedor dividem máquina.
export function phReset(): void {
  comPostHog((p) => p.reset());
}
